import React from 'react';
import { FormattedMessage } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { Typography } from '@mui/material';

import { TranslationKeys } from '../../../../../../../../translations';

import type { Owner, OwnerEmployer } from '../../../../../../../../types';

import { ActionButton, Dialog } from '../../../../../../../../components';

import type { UserAccessesListSelectedData } from '../../UserAccessesList.types';

import {
  HierarchicalItemStyled,
  NestedItemStyled,
  NestedListStyled,
  SubNestedItemStyled,
  UnorderedListStyled,
} from './UserAccessesListConfirmDialog.styles';

type UserAccessesListConfirmDialogProps = {
  show: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedData: UserAccessesListSelectedData;
  owner: Owner | undefined;
};

/** Renders a list of selected employers with their sub-employers nested underneath */
const EmployerNestedList = ({
  employerIds,
  selectedEmployerIdSet,
  employerNames,
  employerChildren,
}: {
  employerIds: string[];
  selectedEmployerIdSet: Set<string>;
  employerNames: Record<string, string>;
  employerChildren: Record<string, string[]>;
}) => {
  if (employerIds.length === 0) return null;

  return (
    <NestedListStyled>
      {employerIds.map(employerId => {
        const employerName = employerNames[employerId];
        if (!employerName) return null;

        const children = (employerChildren[employerId] || []).filter(childId => selectedEmployerIdSet.has(childId));

        return (
          <NestedItemStyled key={employerId}>
            <Typography variant={'body2'}>{employerName}</Typography>
            {children.length > 0 && (
              <NestedListStyled>
                {children.map(childId => (
                  <SubNestedItemStyled key={childId}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                      {employerNames[childId]}
                    </Typography>
                  </SubNestedItemStyled>
                ))}
              </NestedListStyled>
            )}
          </NestedItemStyled>
        );
      })}
    </NestedListStyled>
  );
};

export const UserAccessesListConfirmDialog = ({
  show,
  isLoading,
  onConfirm,
  onClose,
  selectedData,
  owner,
}: UserAccessesListConfirmDialogProps) => {
  /**
   * Build lookup maps from the Owner object:
   * - subOwnerNames: subOwnerId -> name
   * - subOwnerEmployerIds: subOwnerId -> Set of employerIds that belong to it
   * - employerNames: employerId -> name (from all sources)
   * - employerChildren: employerId -> array of child employerIds (based on parentId)
   */
  const ownerMaps = React.useMemo(() => {
    const subOwnerNames: Record<string, string> = {};
    const subOwnerEmployerIds: Record<string, Set<string>> = {};
    const employerNames: Record<string, string> = {};
    const employerChildren: Record<string, string[]> = {};

    const registerEmployer = (employer: OwnerEmployer) => {
      employerNames[employer.employerId] = employer.employerName;
      if (employer.parentId) {
        if (!employerChildren[employer.parentId]) {
          employerChildren[employer.parentId] = [];
        }
        employerChildren[employer.parentId].push(employer.employerId);
      }
    };

    // Register top-level employers from owner
    owner?.employers?.forEach(registerEmployer);

    // Register subOwners and their employers
    owner?.subOwners?.forEach(subOwner => {
      subOwnerNames[subOwner.ownerId] = subOwner.ownerName;
      subOwnerEmployerIds[subOwner.ownerId] = new Set();
      subOwner.employers?.forEach(employer => {
        subOwnerEmployerIds[subOwner.ownerId].add(employer.employerId);
        registerEmployer(employer);
      });
    });

    return { subOwnerNames, subOwnerEmployerIds, employerNames, employerChildren };
  }, [owner]);

  const selectedSubOwnerIds = React.useMemo(
    () => (typeof selectedData.subOwners === 'string' ? [] : Object.keys(selectedData.subOwners || {})),
    [selectedData.subOwners],
  );

  const selectedEmployerIdSet = React.useMemo(
    () => new Set(typeof selectedData.employers === 'string' ? [] : Object.keys(selectedData.employers || {})),
    [selectedData.employers],
  );

  /**
   * Set of employer IDs that are displayed under a subOwner in the first section,
   * so they are excluded from the standalone employers section.
   */
  const employersShownInSubOwners = React.useMemo(() => {
    const shown = new Set<string>();

    selectedSubOwnerIds.forEach(subOwnerId => {
      const employerIdsForSubOwner = ownerMaps.subOwnerEmployerIds[subOwnerId];
      if (!employerIdsForSubOwner) return;

      employerIdsForSubOwner.forEach(employerId => {
        if (selectedEmployerIdSet.has(employerId)) {
          shown.add(employerId);
          // Also mark children of this employer that are selected
          (ownerMaps.employerChildren[employerId] || []).forEach(childId => {
            if (selectedEmployerIdSet.has(childId)) {
              shown.add(childId);
            }
          });
        }
      });
    });

    return shown;
  }, [selectedSubOwnerIds, selectedEmployerIdSet, ownerMaps]);

  const subOwnerAccesses = React.useMemo(() => {
    // Check if all subOwners are selected
    if (typeof selectedData.subOwners === 'string' && selectedData.subOwners === '*') {
      return (
        <Typography variant={'body2'}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_accessToAllSubEmployers} />
        </Typography>
      );
    }

    if (selectedSubOwnerIds.length === 0) {
      return (
        <Typography variant={'caption'} textTransform={'initial'}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_noAccesses} />
        </Typography>
      );
    }

    const items = selectedSubOwnerIds
      .map(subOwnerId => {
        const subOwnerName = ownerMaps.subOwnerNames[subOwnerId];
        if (!subOwnerName) return null;

        const employerIdsForSubOwner = ownerMaps.subOwnerEmployerIds[subOwnerId];

        // Get selected employers belonging to this subOwner
        const selectedEmployersForSubOwner = employerIdsForSubOwner
          ? Array.from(employerIdsForSubOwner).filter(id => selectedEmployerIdSet.has(id))
          : [];

        // Root employers: those without a parentId, or whose parent is not among the
        // selected employers for this subOwner (so they appear at the top level)
        const rootEmployerIds = selectedEmployersForSubOwner.filter(employerId => {
          const children = ownerMaps.employerChildren;
          // An employer is a root if no other selected employer in this subOwner is its parent
          return !selectedEmployersForSubOwner.some(otherId => (children[otherId] || []).includes(employerId));
        });

        return (
          <HierarchicalItemStyled key={subOwnerId}>
            <Typography fontWeight={'bold'}>{subOwnerName}</Typography>
            <EmployerNestedList
              employerIds={rootEmployerIds}
              selectedEmployerIdSet={selectedEmployerIdSet}
              employerNames={ownerMaps.employerNames}
              employerChildren={ownerMaps.employerChildren}
            />
          </HierarchicalItemStyled>
        );
      })
      .filter(Boolean);

    if (items.length === 0) {
      return (
        <Typography variant={'caption'} textTransform={'initial'}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_noAccesses} />
        </Typography>
      );
    }

    return items;
  }, [selectedSubOwnerIds, selectedEmployerIdSet, selectedData.subOwners, ownerMaps]);

  const employerAccesses = React.useMemo(() => {
    // Check if all employers are selected
    if (typeof selectedData.employers === 'string' && selectedData.employers === '*') {
      return (
        <Typography variant={'body2'}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_accessToAllEmployers} />
        </Typography>
      );
    }

    // Employers that are selected but NOT already shown under a subOwner
    const remainingEmployerIds = Array.from(selectedEmployerIdSet).filter(
      id => !employersShownInSubOwners.has(id) && ownerMaps.employerNames[id],
    );

    // Root employers among the remaining: those whose parent is not in the remaining set
    const rootEmployerIds = remainingEmployerIds.filter(employerId => {
      return !remainingEmployerIds.some(otherId => (ownerMaps.employerChildren[otherId] || []).includes(employerId));
    });

    if (rootEmployerIds.length === 0) {
      return (
        <Typography variant={'caption'} textTransform={'initial'}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_noAccesses} />
        </Typography>
      );
    }

    const items = rootEmployerIds
      .map(employerId => {
        const employerName = ownerMaps.employerNames[employerId];
        if (!employerName) return null;

        // Children that are also in the remaining set
        const remainingSet = new Set(remainingEmployerIds);
        const children = (ownerMaps.employerChildren[employerId] || []).filter(childId => remainingSet.has(childId));

        return (
          <HierarchicalItemStyled key={employerId}>
            <Typography>{employerName}</Typography>
            {children.length > 0 && (
              <NestedListStyled>
                {children.map(childId => (
                  <SubNestedItemStyled key={childId}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                      {ownerMaps.employerNames[childId]}
                    </Typography>
                  </SubNestedItemStyled>
                ))}
              </NestedListStyled>
            )}
          </HierarchicalItemStyled>
        );
      })
      .filter(Boolean);

    if (items.length === 0) {
      return (
        <Typography variant={'caption'} textTransform={'initial'}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_noAccesses} />
        </Typography>
      );
    }

    return items;
  }, [selectedEmployerIdSet, employersShownInSubOwners, selectedData.employers, ownerMaps]);

  const topLevelEmployersCount = React.useMemo(() => {
    const remainingEmployerIds = Array.from(selectedEmployerIdSet).filter(
      id => !employersShownInSubOwners.has(id) && ownerMaps.employerNames[id],
    );

    return remainingEmployerIds.filter(employerId => {
      return !remainingEmployerIds.some(otherId => (ownerMaps.employerChildren[otherId] || []).includes(employerId));
    }).length;
  }, [selectedEmployerIdSet, employersShownInSubOwners, ownerMaps]);

  const counts = React.useMemo(() => {
    let subOwnersCount = 0;
    let employersCount = 0;

    if (typeof selectedData.subOwners === 'string' && selectedData.subOwners === '*') {
      subOwnersCount = owner?.subOwners?.length || 0;
    } else {
      subOwnersCount = Object.keys(selectedData.subOwners || {}).length;
    }

    if (typeof selectedData.employers === 'string' && selectedData.employers === '*') {
      employersCount = owner?.employers?.length || 0;
    } else {
      employersCount = topLevelEmployersCount;
    }

    return { subOwnersCount, employersCount };
  }, [selectedData, owner, topLevelEmployersCount]);

  const DialogActions = React.useMemo(() => {
    return (
      <>
        <ActionButton messageId={TranslationKeys.no} disabled={isLoading} onClick={onClose} variant={'outlined'} />
        <ActionButton messageId={TranslationKeys.yes} disabled={isLoading} onClick={onConfirm} />
      </>
    );
  }, [isLoading, onClose, onConfirm]);

  return (
    <Dialog
      open={show}
      maxWidth={'md'}
      title={<FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_title} />}
      onClose={onClose}
      actions={DialogActions}
      fullWidth
    >
      <>
        <Typography>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_header} />
        </Typography>

        <Typography variant={'h6'} marginTop={2} marginBottom={1}>
          <FormattedMessage
            id={TranslationKeys.users_accessesList_subOwners}
            values={{ count: counts.subOwnersCount }}
          />
        </Typography>

        <UnorderedListStyled>
          <PerfectScrollbar>{subOwnerAccesses}</PerfectScrollbar>
        </UnorderedListStyled>

        <Typography variant={'h6'} marginTop={2} marginBottom={1}>
          <FormattedMessage
            id={TranslationKeys.users_accessesList_employers}
            values={{ count: counts.employersCount }}
          />
        </Typography>

        <UnorderedListStyled>
          <PerfectScrollbar>{employerAccesses}</PerfectScrollbar>
        </UnorderedListStyled>
      </>
    </Dialog>
  );
};
